// /app/frontend/src/components/admin/FiliaisManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiUsers, FiShoppingBag, FiKey, FiEye, FiEyeOff, FiRefreshCw,
  FiCheck, FiX, FiAlertTriangle, FiSearch, FiMail, FiPhone,
  FiPower, FiEdit, FiShield
} from 'react-icons/fi';
import api from '@/services/api';

const FiliaisManager = () => {
  const [filiais, setFiliais] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('filiais');
  const [search, setSearch] = useState('');
  
  // Estados para modal de reset de senha
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedFilial, setSelectedFilial] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [filiaisRes, adminsRes] = await Promise.all([
        api.get('/cerebro/filiais'),
        api.get('/cerebro/admins')
      ]);
      setFiliais(filiaisRes.data.filiais || []);
      setAdmins(adminsRes.data.admins || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleStatus = async (filialId) => {
    try {
      await api.post(`/cerebro/filiais/${filialId}/toggle-status`);
      loadData();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert('Erro ao alterar status da filial');
    }
  };

  const openResetModal = (filial) => {
    setSelectedFilial(filial);
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetSuccess('');
    setShowResetModal(true);
  };

  const handleResetPassword = async () => {
    setResetError('');
    setResetSuccess('');

    // Validações
    if (!newPassword || !confirmPassword) {
      setResetError('Preencha todos os campos');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 8) {
      setResetError('A senha deve ter pelo menos 8 caracteres');
      return;
    }

    // Verificar requisitos de senha forte
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecial) {
      setResetError('A senha deve conter: maiúscula, minúscula, número e caractere especial');
      return;
    }

    setResetting(true);
    try {
      const response = await api.post(`/cerebro/filiais/${selectedFilial.id}/reset-password`, {
        new_password: newPassword
      });
      
      setResetSuccess(`Senha redefinida com sucesso para ${response.data.admin?.name || 'o administrador'}`);
      setTimeout(() => {
        setShowResetModal(false);
        setResetSuccess('');
      }, 2000);
    } catch (error) {
      setResetError(error.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setResetting(false);
    }
  };

  const filteredFiliais = filiais.filter(f => 
    f.name?.toLowerCase().includes(search.toLowerCase()) ||
    f.admin?.email?.toLowerCase().includes(search.toLowerCase()) ||
    f.admin?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredAdmins = admins.filter(a =>
    a.name?.toLowerCase().includes(search.toLowerCase()) ||
    a.email?.toLowerCase().includes(search.toLowerCase()) ||
    a.filial?.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="filiais-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          <FiShoppingBag className="text-red-500" />
          Gerenciamento de Filiais
        </h3>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
        >
          <FiRefreshCw size={14} />
          Atualizar
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('filiais')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'filiais'
              ? 'text-red-600 border-b-2 border-red-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <FiShoppingBag className="inline mr-2" />
          Filiais ({filiais.length})
        </button>
        <button
          onClick={() => setActiveTab('admins')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'admins'
              ? 'text-red-600 border-b-2 border-red-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <FiUsers className="inline mr-2" />
          Administradores ({admins.length})
        </button>
      </div>

      {/* Busca */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por nome, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
        />
      </div>

      {/* Lista de Filiais */}
      {activeTab === 'filiais' && (
        <div className="space-y-3">
          {filteredFiliais.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FiShoppingBag className="mx-auto text-4xl mb-2" />
              <p>Nenhuma filial encontrada</p>
            </div>
          ) : (
            filteredFiliais.map((filial) => (
              <div
                key={filial.id}
                className={`bg-white p-4 rounded-xl border shadow-sm ${
                  !filial.is_active ? 'opacity-60' : ''
                }`}
                data-testid={`filial-${filial.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-gray-800">{filial.name}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        filial.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {filial.is_active ? 'Ativa' : 'Inativa'}
                      </span>
                    </div>
                    
                    {filial.admin && (
                      <div className="mt-2 text-sm text-gray-600">
                        <p className="flex items-center gap-1">
                          <FiUsers size={14} />
                          <span className="font-medium">{filial.admin.name}</span>
                        </p>
                        <p className="flex items-center gap-1">
                          <FiMail size={14} />
                          {filial.admin.email}
                        </p>
                      </div>
                    )}

                    <div className="mt-2 flex gap-4 text-xs text-gray-500">
                      <span>📦 {filial.stats?.products || 0} produtos</span>
                      <span>🛒 {filial.stats?.orders || 0} pedidos</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => openResetModal(filial)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 text-sm"
                      title="Redefinir senha do administrador"
                      data-testid={`reset-password-${filial.id}`}
                    >
                      <FiKey size={14} />
                      Redefinir Senha
                    </button>
                    <button
                      onClick={() => handleToggleStatus(filial.id)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                        filial.is_active
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                      title={filial.is_active ? 'Desativar filial' : 'Ativar filial'}
                    >
                      <FiPower size={14} />
                      {filial.is_active ? 'Desativar' : 'Ativar'}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Lista de Administradores */}
      {activeTab === 'admins' && (
        <div className="space-y-3">
          {filteredAdmins.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FiUsers className="mx-auto text-4xl mb-2" />
              <p>Nenhum administrador encontrado</p>
            </div>
          ) : (
            filteredAdmins.map((admin) => (
              <div
                key={admin.id}
                className="bg-white p-4 rounded-xl border shadow-sm"
                data-testid={`admin-${admin.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-gray-800">{admin.name}</h4>
                      <span className={`w-2 h-2 rounded-full ${
                        admin.status_online ? 'bg-green-500' : 'bg-gray-400'
                      }`} title={admin.status_online ? 'Online' : 'Offline'}></span>
                    </div>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <FiMail size={14} />
                      {admin.email}
                    </p>
                    {admin.whatsapp && (
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <FiPhone size={14} />
                        {admin.whatsapp}
                      </p>
                    )}
                    {admin.filial && (
                      <p className="text-sm text-gray-500 mt-1">
                        <FiShoppingBag className="inline mr-1" />
                        {admin.filial.name}
                        {!admin.filial.is_active && (
                          <span className="text-red-500 ml-1">(Inativa)</span>
                        )}
                      </p>
                    )}
                    {admin.last_login && (
                      <p className="text-xs text-gray-400 mt-1">
                        Último acesso: {new Date(admin.last_login).toLocaleString('pt-BR')}
                      </p>
                    )}
                  </div>

                  {admin.filial && (
                    <button
                      onClick={() => openResetModal({
                        id: admin.filial.id,
                        name: admin.filial.name,
                        admin: admin
                      })}
                      className="flex items-center gap-1 px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 text-sm"
                    >
                      <FiKey size={14} />
                      Redefinir Senha
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal de Reset de Senha */}
      {showResetModal && selectedFilial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl" data-testid="reset-password-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FiShield className="text-yellow-500" />
                Redefinir Senha Master
              </h3>
              <button
                onClick={() => setShowResetModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-700">
                <FiAlertTriangle className="inline mr-1" />
                Você está redefinindo a senha do administrador da filial:
              </p>
              <p className="font-bold text-yellow-800 mt-1">{selectedFilial.name}</p>
              {selectedFilial.admin && (
                <p className="text-sm text-yellow-700">
                  Admin: {selectedFilial.admin.name} ({selectedFilial.admin.email})
                </p>
              )}
            </div>

            {resetError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700 flex items-center gap-1">
                  <FiX />
                  {resetError}
                </p>
              </div>
            )}

            {resetSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-green-700 flex items-center gap-1">
                  <FiCheck />
                  {resetSuccess}
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10"
                    placeholder="Digite a nova senha"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar Senha
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Confirme a nova senha"
                  data-testid="confirm-password-input"
                />
              </div>

              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                <p className="font-medium mb-1">Requisitos da senha:</p>
                <ul className="space-y-1">
                  <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>
                    {newPassword.length >= 8 ? '✓' : '○'} Mínimo 8 caracteres
                  </li>
                  <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>
                    {/[A-Z]/.test(newPassword) ? '✓' : '○'} Uma letra maiúscula
                  </li>
                  <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>
                    {/[a-z]/.test(newPassword) ? '✓' : '○'} Uma letra minúscula
                  </li>
                  <li className={/\d/.test(newPassword) ? 'text-green-600' : ''}>
                    {/\d/.test(newPassword) ? '✓' : '○'} Um número
                  </li>
                  <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'text-green-600' : ''}>
                    {/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? '✓' : '○'} Um caractere especial
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowResetModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                disabled={resetting}
              >
                Cancelar
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resetting || !newPassword || !confirmPassword}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="confirm-reset-button"
              >
                {resetting ? (
                  <>
                    <FiRefreshCw className="animate-spin" />
                    Redefinindo...
                  </>
                ) : (
                  <>
                    <FiKey />
                    Redefinir Senha
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiliaisManager;
